/**
 * Provides the `processXML` utility function for processing XML data, and its associated `ProcessXmlData` type.
 * @module processXML
 */
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
/**
 * Processes an XML string to extract the root element's tag name and the count of its direct child elements.
 *
 * **Behavior:**
 * - Uses the browser's built-in `DOMParser` to parse the input `xmlText` string.
 * - Checks for parser errors reported by the browser's parser.
 * - Identifies the root element (`documentElement`).
 * - Extracts the root element's tag name (`nodeName`).
 * - Counts the number of direct child *elements* using `rootElement.children.length`.
 * - Returns an object `{ type: 'xml', rootElementName: string, numberOfChildElements: number }`.
 *
 * **Error handling:**
 * - Throws an error if the input `xmlText` is null, undefined, or empty.
 * - Throws an error if the `DOMParser` reports a parsing error (e.g., invalid XML syntax).
 * - May throw errors if the XML structure is highly unusual or lacks a root element after parsing,
 * though the parser error check aims to catch most syntax issues.
 *
 * @since 5.10 Initial implementation.
 * @param {string | null | undefined} xmlText - The XML data as a string. Can be null or undefined.
 * @return {ProcessXmlData} An object containing the `rootElementName` and `numberOfChildElements`.
 * @throws {Error} If the XML text is invalid (null, empty, whitespace-only, or contains parsing errors).
 *
 * @example Importing the processXML utility
 * ```typescript
 * import { processXML } from '@automator/shared/utils/get-file-data/process-xml';
 * ```
 *
 * @example Process XML coming from the server
 * If you want to process a XML coming from the server, use the `getFileData` utility instead of `processXML`.
 * ```typescript
 * const xmlData = await getFileData( 'xml', 'https://example.com/data.xml' );
 * ```
 *
 * @example Processing valid XML
 * ```typescript
 * const xmlString = '<root><item id="1"/><item id="2"><child/></item></root>';
 * processXML( xmlString );
 * // Returns: { type: 'xml', rootElementName: 'root', numberOfChildElements: 2 }
 * ```
 *
 * @example Processing XML with no child elements
 * ```typescript
 * const simpleXml = '<data/>';
 * processXML( simpleXml );
 * // Returns: { type: 'xml', rootElementName: 'data', numberOfChildElements: 0 }
 * ```
 *
 * @example Handling invalid XML input
 * ```typescript
 * const invalidXml = '<root><item></root>'; // Malformed
 *
 * try {
 *     processXML( invalidXml );
 * } catch ( error ) {
 *     console.error( error ); // Catches Error: Invalid XML
 * }
 * ```
 *
 * @example Handling empty or null input
 * ```typescript
 * try {
 *     processXML( null ); // Or '', undefined, '  \n  '
 * } catch ( error ) {
 *     console.error( error ); // Catches Error: Input XML text cannot be null, undefined, or empty.
 * }
 * ```
 */
export function processXML(xmlText) {
    // Check for null, undefined, empty, or whitespace-only strings
    if (isEmpty(xmlText) || (typeof xmlText === 'string' && xmlText.trim() === '')) {
        throw new Error('Input XML text cannot be null, undefined, or empty.');
    }
    // Ensure DOMParser is available (should be in browser environments)
    if (typeof DOMParser === 'undefined') {
        throw new Error('DOMParser is not available in this environment.');
    }
    const parser = new DOMParser();
    // Parse the XML string. DOMParser returns an XMLDocument.
    const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
    // This is a standard way to check for parser errors in XML parsing
    // Browsers often insert a <parsererror> element upon failure.
    // Check https://developer.mozilla.org/en-US/docs/Web/XML/Guides/Parsing_and_serializing_XML
    const parserErrors = xmlDoc.getElementsByTagName('parsererror');
    if (parserErrors.length > 0) {
        const errorDetails = parserErrors[0]?.textContent || 'Unknown parsing error';
        throw new Error(`Invalid XML: ${errorDetails}`);
    }
    // Get the root element
    const rootElement = xmlDoc.documentElement;
    // Check if a root element exists (might not if XML is truly empty or malformed despite no parsererror)
    if (!rootElement) {
        throw new Error('Could not find root element in the XML document.');
    }
    const rootElementName = rootElement.nodeName;
    const numberOfChildElements = rootElement.children.length;
    return {
        type: 'xml',
        rootElementName: rootElementName,
        numberOfChildElements: numberOfChildElements,
    };
}
;
